<script setup>
import { ref, watch } from 'vue'
import { logOnDev } from '@/assets/js/main'
import { useCommonStore } from '@/stores/commonStore'

// PROPS
const props = defineProps({
  modelValue: {
    type: Object,
    default: null
  },
  optionLabel: {
    type: String,
    default: '',
    required: true
  },
  placeholder: {
    type: String,
    default: 'Cerca...',
    required: true
  },
  label: {
    type: String,
    default: '',
    required: true
  },
  apiEndpoint: {
    type: String,
    default: '',
    required: true
  },
  otherParams: {
    type: Map,
    default: () => new Map()
  },
  scope: {
    type: String,
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const currentStore = useCommonStore()
const items = ref([])
const loading = ref(false)
const page = ref(0)
const lastQuery = ref('')
const hasMore = ref(false)

const emit = defineEmits(['update:modelValue'])
const selectedItem = ref(props.modelValue)

// quando cambia la prop da fuori, aggiorno lo stato interno
watch(
  () => props.modelValue,
  (newVal) => {
    selectedItem.value = newVal
  }
)

// quando cambia internamente, emetto verso il genitore
watch(selectedItem, (val) => {
  emit('update:modelValue', val)
})

const fetchResults = async (query, reset = false) => {
  if (!query || query.length < 2) {
    items.value = []
    hasMore.value = false
    return
  }

  loading.value = true

  try {
    const response = await currentStore.getItems(
      props.apiEndpoint,
      page.value,
      50,
      null,
      query,
      new Map([...props.otherParams])
    )

    const responseData = response.data
    const responseCount = response.count
    const responsePage = response.page
    const responsePerPage = response.perPage

    if (reset) {
      items.value = responseData
    } else {
      items.value = [...items.value, ...responseData]
    }

    logOnDev(responseCount)
    logOnDev(responsePage)
    logOnDev(responsePerPage)

    hasMore.value = responsePage < responseCount / responsePerPage
    page.value = responsePage + 1
    logOnDev(`Page: ${page.value}, hasMore: ${hasMore.value}`)
  } catch (err) {
    console.error(err)
  } finally {
    loading.value = false
  }
}

// initial search
const search = (event) => {
  page.value = 0
  lastQuery.value = event.query
  fetchResults(event.query, true)
}

// footer load more call
const loadMore = () => {
  if (hasMore.value && !loading.value) {
    fetchResults(lastQuery.value, false)
  }
}

const resetSearch = () => {
  selectedItem.value = null // reset campo input
  items.value = [] // svuota suggerimenti
  lastQuery.value = '' // reset query
  page.value = 0 // reset paginazione
  hasMore.value = false
}

// 👇 espone il metodo al genitore
defineExpose({
  resetSearch
})
</script>

<template>
  <FloatLabel>
    <AutoComplete
      id="autocomplete"
      v-model="selectedItem"
      :suggestions="items"
      :option-label="optionLabel"
      :force-selection="true"
      :min-length="2"
      :dropdown="true"
      :placeholder="placeholder"
      :disabled="disabled"
      @complete="search"
    >
      <template #empty>
        <div class="p-2 text-gray-400 text-sm text-center">
          Nessun risultato
        </div>
      </template>
      <!-- loader sotto / Load more -->
      <template #footer>
        <div v-if="loading" class="flex justify-center items-center p-2">
          <i class="pi pi-spin pi-spinner mr-2"></i>
          <span>Caricamento...</span>
        </div>
        <div
          v-else-if="hasMore"
          class="text-center p-2 cursor-pointer text-blue-600 hover:underline"
          @mousedown.prevent="loadMore"
        >
          Carica altri risultati
        </div>

        <div v-else class="text-center p-2 text-gray-400 text-sm">
          Nessun altro risultato
        </div>
      </template>
    </AutoComplete>
    <label v-if="selectedItem" for="autocomplete">{{ label }}</label>
  </FloatLabel>
</template>
